import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart } from 'lucide-react';
import api from '../../services/api';

const FeaturedSystemSection = ({ content }) => {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  const targetSlug = content?.product_slug || 'emotional-regulation-toolkit';
  const supportingCopy = content?.supporting_copy || 'Teach your child how to recognize emotions, calm down faster, and respond with confidence. This toolkit provides simple parent-guided tools to help children understand feelings, regulate reactions, and build lifelong emotional intelligence.';
  const ctaText = content?.cta_text || 'Explore Toolkit';
  const heading = content?.heading || 'Emotional Regulation Toolkit';
  const eyebrow = content?.eyebrow || 'Featured System';

  useEffect(() => {
    const fetchFeaturedProduct = async () => {
      try {
        const data = await api.getProducts();
        const products = data.products || data;
        const match = products.find(p =>
          p.slug === targetSlug ||
          (p.name || '').toLowerCase().includes(targetSlug.replace(/-/g, ' '))
        );
        setProduct(match || products[0]);
      } catch (error) {
        console.error('Error fetching featured product:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFeaturedProduct();
  }, [targetSlug]);

  if (loading) {
    return (
      <section className="py-16 lg:py-24 bg-[#F8FAFC]">
        <div className="wrap">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-pulse bg-[#E2E8F0] rounded-2xl aspect-[4/5]"></div>
            <div className="space-y-4">
              <div className="h-6 w-32 bg-[#E2E8F0] rounded"></div>
              <div className="h-10 w-3/4 bg-[#E2E8F0] rounded"></div>
              <div className="h-24 bg-[#E2E8F0] rounded"></div>
              <div className="h-12 w-40 bg-[#E2E8F0] rounded-full"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!product) return null;

  return (
    <section className="py-16 lg:py-24 bg-[#F8FAFC] relative overflow-hidden" data-testid="featured-system-section">
      {/* Subtle Background Accent Shape */}
      <div className="absolute top-0 right-0 w-1/2 h-full opacity-[0.03]">
        <svg viewBox="0 0 400 600" fill="none" className="w-full h-full">
          <circle cx="400" cy="300" r="350" fill="#0F766E"/>
        </svg>
      </div>

      <div className="wrap relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 text-[#0F766E] font-semibold text-sm uppercase tracking-wider mb-3">
            <Heart className="h-4 w-4" />
            <span>{eyebrow}</span>
          </div>
        </div>

        {/* Two-Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center max-w-6xl mx-auto">
          {/* Left Side: Product Cover Image */}
          <div className="order-1 lg:order-1">
            <div className="relative">
              {/* Product Image */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-[#0F766E]/10 bg-white">
                <img 
                  src={product.image} 
                  alt={product.name}
                  className="w-full aspect-[4/5] object-cover"
                  width="480"
                  height="600"
                  decoding="async"
                  loading="lazy"
                  data-testid="featured-product-image"
                />
              </div>
              
              {/* Decorative Element */}
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-[#0F766E]/10 rounded-2xl -z-10"></div>
              <div className="absolute -top-4 -left-4 w-16 h-16 bg-[#0F766E]/5 rounded-xl -z-10"></div>
            </div>
          </div>

          {/* Right Side: Text Content */}
          <div className="order-2 lg:order-2 text-center lg:text-left">
            {/* Product Title */}
            <h2 className="text-3xl lg:text-[40px] font-bold text-[#0B1F3A] mb-6 leading-tight">
              {heading}
            </h2>

            {/* Product Description */}
            <p className="text-lg text-[#475569] mb-8 leading-relaxed max-w-lg mx-auto lg:mx-0">
              {supportingCopy}
            </p>

            {/* Key Benefits (Optional Enhancement) */}
            <div className="flex flex-wrap gap-3 justify-center lg:justify-start mb-8">
              {['Ages 4-12', 'Instant Download', 'Parent-Guided'].map((tag) => (
                <span 
                  key={tag}
                  className="px-4 py-2 bg-white rounded-full text-sm font-medium text-[#475569] border border-[#E2E8F0]"
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* Price */}
            {product.price && (
              <p className="text-2xl font-bold text-[#0F766E] mb-6">
                ${product.price}
              </p>
            )}

            {/* CTA Button */}
            <Link 
              to={`/product/${product.slug || product.id}`}
              className="group inline-flex items-center justify-center gap-3 bg-[#0F766E] hover:bg-[#0D6B64] text-white font-semibold px-8 py-4 rounded-full transition-all shadow-lg shadow-[#0F766E]/20 hover:shadow-[#0F766E]/30 hover:-translate-y-0.5"
              data-testid="featured-system-cta"
            >
              {ctaText}
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedSystemSection;
